import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, AlertTriangle, Lock, ShieldAlert, Mail, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [recoveryInfo, setRecoveryInfo] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const handleLoginResult = (result) => {
    // Admins must use the dedicated admin portal
    if (result.user?.is_admin) {
      setErrorMessage('Admin accounts must sign in via the Admin Portal.');
      toast.info('Redirecting to Admin Portal...');
      navigate('/admin-login');
      return;
    }

    // Check if this is a recovered account
    if (result.account_recovered && result.needs_password_reset) {
      setRecoveryInfo({
        message: result.recovery_message,
        email: email
      });
      toast.warning('Account recovered - please reset your password');
      // Don't navigate yet, show the recovery modal
    } else if (result.needs_email_verification) {
      toast.info('Please verify your email to continue');
      navigate('/verify-email');
    } else if (result.needs_profile_completion) {
      toast.info('Please complete your profile to continue');
      navigate('/profile', {
        state: { profileCompletion: true }
      });
    } else {
      toast.success('Welcome back!');
      navigate('/dashboard', {
        state: { showOnboarding: result.show_onboarding }
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLockoutMessage('');
    setErrorMessage('');
    setRecoveryInfo(null);
    setPendingDeletion(null);
    try {
      const result = await login(email, password);
      handleLoginResult(result);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Login failed';

      if (status === 403 && typeof detail === 'object' && detail?.type === 'pending_deletion') {
        setPendingDeletion(detail);
        return;
      }
      const message = typeof detail === 'string' ? detail : 'Login failed';
      if (status === 423) {
        setLockoutMessage(message);
        toast.error('Account locked');
      } else {
        setErrorMessage(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreAccount = async () => {
    setRestoring(true);
    try {
      await axios.post(`${API_URL}/api/auth/restore-account`, { email, password });
      toast.success('Your account has been restored!');
      setPendingDeletion(null);
      const result = await login(email, password);
      handleLoginResult(result);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Unable to restore account');
    } finally {
      setRestoring(false);
    }
  };

  const handleContinueWithoutReset = () => {
    toast.info('You can reset your password anytime from settings');
    navigate('/dashboard', { state: { showOnboarding: true } });
  };

  const handleResetPassword = () => {
    navigate('/forgot-password', { state: { email: recoveryInfo?.email } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-primary/5 to-transparent">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Pending Deletion - Restore Panel */}
        {pendingDeletion && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-amber-50 border border-amber-300 rounded-2xl p-6 mb-6"
            data-testid="pending-deletion-panel"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-base text-amber-800 font-semibold mb-2">Account scheduled for deletion</p>
                <p className="text-sm text-amber-700 mb-4" data-testid="pending-deletion-message">
                  {pendingDeletion.message}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleRestoreAccount}
                    disabled={restoring}
                    className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="restore-account-button"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {restoring ? 'Restoring...' : 'Restore My Account'}
                  </Button>
                  <Button
                    onClick={() => setPendingDeletion(null)}
                    variant="outline"
                    className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="keep-deletion-button"
                  >
                    Keep Deletion Scheduled
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Inline Login Error */}
        {errorMessage && !lockoutMessage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6"
            data-testid="login-error-message"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-700">{errorMessage}</p>
                {errorMessage.toLowerCase().includes('admin portal') && (
                  <Link
                    to="/admin-login"
                    data-testid="go-to-admin-portal-link"
                    className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-red-600 hover:underline"
                  >
                    Go to Admin Portal →
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Account Lockout Warning */}
        {lockoutMessage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6"
          >
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-semibold">Account Locked</p>
                <p className="text-sm text-red-700">{lockoutMessage}</p>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-red-600 font-semibold hover:underline inline-flex items-center gap-1 mt-2"
                >
                  Reset your password →
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Recovered Account Notification */}
        {recoveryInfo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-6"
          >
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-base text-blue-800 font-semibold mb-2">Account Recovered</p>
                <p className="text-sm text-blue-700 mb-4">
                  {recoveryInfo.message || 'Your account was recovered after system maintenance. For security, we recommend resetting your password.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleResetPassword}
                    className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="reset-password-button"
                  >
                    Reset Password (Recommended)
                  </Button>
                  <Button
                    onClick={handleContinueWithoutReset}
                    variant="outline"
                    className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid="continue-without-reset-button"
                  >
                    Continue Without Resetting
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex justify-center items-center gap-2 mb-4">
            <span className="text-3xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome Back</h1>
          <p className="text-base text-muted-foreground">Continue your nutrition journey</p>
        </div>

        <div className="bg-card text-card-foreground rounded-2xl border border-border/50 shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-xl border-border bg-background/50 focus:ring-2 focus:ring-ring focus:border-transparent px-4 py-3 shadow-sm transition-all"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-xl border-border bg-background/50 focus:ring-2 focus:ring-ring focus:border-transparent px-4 py-3 shadow-sm transition-all"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          {/* Can't log in button */}
          <div className="mt-6 pt-6 border-t border-border/50">
            <Button
              variant="ghost"
              className="w-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={() => navigate('/login-help')}
              data-testid="cant-login-button"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              I can't log in
            </Button>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary font-semibold hover:underline">
              Sign Up
            </Link>
          </p>
        </div>
      </motion.div>

      {/* Footer */}
      <footer className="absolute bottom-4 left-0 right-0 text-center">
        <div className="inline-flex items-center gap-6">
          <Link 
            to="/contact-us" 
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 font-medium"
            data-testid="contact-us-link"
          >
            <Mail className="w-4 h-4" />
            Contact Us
          </Link>
          <Link 
            to="/admin-login" 
            className="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
            data-testid="footer-admin-link"
          >
            <ShieldAlert className="w-4 h-4" />
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;